import type { ActivityItem } from "../../domain/app-types.ts";
import type { Todo } from "../../domain/entities.ts";
import { refreshAttachmentView } from "../attachments/refresh-view.ts";
import { refreshPresenceView } from "../collaboration/presence-view.ts";

export interface TaskDetailAttachments {
  load(id: string): Promise<void>;
  count(id: string): number;
  view(id: string, editable: boolean): string;
  bind(root: ParentNode): void;
}

export interface TaskDetailRefreshCapabilities {
  selectedId(): string | null;
  editing(): boolean;
  tasks(): Todo[];
  afterEntry(id: string): Promise<void>;
  canEdit(id: string): boolean;
  canComment(id: string): boolean;
  presence(editable: boolean, commentable: boolean): string;
  request(path: string, init?: RequestInit): Promise<Response>;
  activity(id: string): ActivityItem[] | undefined;
  setActivity(id: string, items: ActivityItem[]): void;
  activityView(id: string): string;
  attachments: TaskDetailAttachments;
}

export interface TaskDetailRefreshController {
  loadActivity(id: string, refresh?: boolean): Promise<void>;
  loadAttachments(id: string): Promise<void>;
  refresh(id: string): Promise<void>;
}

export function createTaskDetailRefreshController(capabilities: TaskDetailRefreshCapabilities): TaskDetailRefreshController {
  const refresh = async (id: string) => {
    if (capabilities.selectedId() !== id || capabilities.editing()) return;
    await capabilities.afterEntry(id);
    if (capabilities.selectedId() !== id || capabilities.editing()) return;
    const todo = capabilities.tasks().find(item => item.id === id); if (!todo) return;
    const editable = capabilities.canEdit(id);
    const presence = document.querySelector<HTMLElement>("[data-task-presence]"); if (presence) refreshPresenceView(presence, capabilities.presence(editable, capabilities.canComment(id)));
    const count = capabilities.attachments.count(id);
    const attachmentHeading = document.querySelector<HTMLElement>("[data-attachment-heading]"); if (attachmentHeading) attachmentHeading.hidden = count === 0;
    const attachmentCount = document.querySelector<HTMLElement>("[data-attachment-count]"); if (attachmentCount) attachmentCount.textContent = String(count);
    const downloadAll = document.querySelector<HTMLButtonElement>("[data-download-attachments]"); if (downloadAll) downloadAll.hidden = count < 2;
    const attachmentTarget = document.querySelector<HTMLElement>("[data-attachment-list]");
    if (attachmentTarget) { refreshAttachmentView(attachmentTarget, capabilities.attachments.view(id, editable)); attachmentTarget.setAttribute("aria-busy", "false"); capabilities.attachments.bind(attachmentTarget); }
    const taskActivity = capabilities.activity(id);
    const activitySection = document.querySelector<HTMLElement>(".activity-section"); if (activitySection) activitySection.hidden = !taskActivity?.length;
    const activityCount = document.querySelector<HTMLElement>("[data-activity-count]"); if (activityCount) activityCount.textContent = String(taskActivity?.length || 0);
    const activityTarget = document.querySelector<HTMLElement>("[data-task-activity]");
    if (activityTarget) {
      const older = activityTarget.querySelector<HTMLDetailsElement>(".activity-older");
      const expanded = !!older?.open, focused = older?.querySelector("summary") === document.activeElement;
      activityTarget.innerHTML = capabilities.activityView(id);
      const next = activityTarget.querySelector<HTMLDetailsElement>(".activity-older");
      if (next && expanded) next.open = true;
      if (focused) next?.querySelector<HTMLElement>("summary")?.focus({ preventScroll: true });
    }
  };
  return {
    loadActivity: async (id, shouldRefresh = true) => { const response = await capabilities.request(`/api/v1/tasks/${id}/activity`, { credentials: "include" }).catch(() => null); if (!response?.ok) return; capabilities.setActivity(id, await response.json()); if (shouldRefresh) await refresh(id); },
    loadAttachments: id => capabilities.attachments.load(id),
    refresh,
  };
}
